import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Property } from '@/types/property';

// Mapeamento dos nomes das cores do Tailwind para valores hexadecimais (apenas tons de azul)
const TAILWIND_COLORS = {
  'blue-base': '#1e90ff',      // Azul vibrante
  'blue-muted': '#4682b4',     // Azul médio
  'blue-deep': '#191970',      // Azul escuro
  'blue-accent': '#00b7eb',    // Azul ciano
  'blue-light': '#87ceeb',     // Azul claro
  'blue-darker': '#00008b',    // Azul mais escuro
  'blue-soft': '#b0e0e6',      // Azul suave
  'neutral-light': '#e6e9f0',
  'neutral-dark': '#2f4f4f',
  'highlight': '#ffa500',
};

// Definindo as cores para o gráfico de pizza (apenas tons de azul)
const COLORS = [
  TAILWIND_COLORS['blue-base'],      // Casa
  TAILWIND_COLORS['blue-muted'],     // Apartamento
  TAILWIND_COLORS['blue-accent'],    // Terreno
  TAILWIND_COLORS['blue-deep'],      // Sobrado
  TAILWIND_COLORS['blue-light'],     // Casa em Condomínio
  TAILWIND_COLORS['blue-darker'],    // Sobrado em Condomínio
  TAILWIND_COLORS['blue-soft'],      // Outros
];

interface PropertyTypeChartProps {
  properties: Property[];
  isMobile?: boolean;
}

const PropertyTypeChart: React.FC<PropertyTypeChartProps> = ({ properties, isMobile = false }) => {
  // Preparar dados para o gráfico de tipos
  const chartData = useMemo(() => {
    // Contar ocorrências de cada tipo
    const typeCount: Record<string, number> = {};
    
    properties.forEach(property => {
      const tipo = property.tipo || 'Não informado';
      typeCount[tipo] = (typeCount[tipo] || 0) + 1;
    });
    
    // Transformar em array para o gráfico
    const data = Object.entries(typeCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    
    // Se houver muitos tipos, agregar os menos comuns em "Outros"
    // No mobile mostramos menos tipos para não sobrecarregar a visualização
    const maxTypes = isMobile ? 4 : 6;
    const topTypes = data.slice(0, maxTypes);
    const otherTypes = data.slice(maxTypes);
    
    if (otherTypes.length > 0) {
      const otherCount = otherTypes.reduce((sum, item) => sum + item.value, 0);
      topTypes.push({ name: 'Outros', value: otherCount });
    }
    
    return topTypes;
  }, [properties, isMobile]);

  // Customizar o tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = ((data.value / properties.length) * 100).toFixed(1);
      
      return (
        <div className="bg-white p-2 border rounded shadow-sm text-xs md:text-sm">
          <p className="font-medium">{data.name}</p>
          <p className="text-xs">{data.value} imóveis ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  // Customizar rótulos
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
    if (isMobile) return null;
    
    const RADIAN = Math.PI / 180;
    // Ajustar o raio para que o rótulo fique mais afastado
    const labelRadius = outerRadius + 30; // Aumentado para evitar colisão
    const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    
    // Ajustar o raio da linha de conexão para terminar antes do rótulo
    const lineEndRadius = outerRadius + 20; // Termina antes do rótulo
    const lineEndX = cx + lineEndRadius * Math.cos(-midAngle * RADIAN);
    const lineEndY = cy + lineEndRadius * Math.sin(-midAngle * RADIAN);
    
    return (
      <>
        {/* Linha de conexão personalizada */}
        <line
          x1={cx + (outerRadius + 5) * Math.cos(-midAngle * RADIAN)}
          y1={cy + (outerRadius + 5) * Math.sin(-midAngle * RADIAN)}
          x2={lineEndX}
          y2={lineEndY}
          stroke={TAILWIND_COLORS['neutral-dark']}
          strokeWidth={1}
        />
        {/* Rótulo */}
        <text
          x={x}
          y={y}
          fill={TAILWIND_COLORS['neutral-dark']}
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize={12}
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      </>
    );
  };

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-xs md:text-sm text-muted-foreground">Dados insuficientes para exibir o gráfico</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false} // Desativamos a labelLine padrão para usar a personalizada
          label={renderCustomizedLabel}
          outerRadius={isMobile ? 60 : 80}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          formatter={(value, entry, index) => {
            return <span className="text-xs md:text-sm">{value}</span>
          }}
          wrapperStyle={isMobile ? { fontSize: '10px' } : { fontSize: '12px' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PropertyTypeChart;